import type { Tasting, Taster } from '../types'

export type WineResult = {
  index: number // letter index (0 = A, 1 = B, etc.)
  letter: string
  name: string | null // null if no wine assigned to this letter
  price?: number
  totalPoints: number
  rank: number
  scoreDistribution: Map<number, string[]> // score -> taster names
}

export function indexToLetter(index: number): string {
  return String.fromCharCode(65 + index) // 65 = 'A'
}

export function calculateResults(tasting: Tasting): WineResult[] {
  // Build results by letter index (scores are per-letter, not per-wine)
  const results: WineResult[] = tasting.wineAssignments.map((wineIndex, letterIndex) => {
    let totalPoints = 0
    const scoreDistribution = new Map<number, string[]>()

    for (const taster of tasting.tasters) {
      const score = taster.scores[letterIndex]
      if (score !== null) {
        totalPoints += score
        const existing = scoreDistribution.get(score) ?? []
        scoreDistribution.set(score, [...existing, taster.name])
      }
    }

    // Resolve wine name and price via assignment
    const assignedWine = wineIndex !== null ? tasting.wines[wineIndex] : null

    return {
      index: letterIndex,
      letter: indexToLetter(letterIndex),
      name: assignedWine?.name ?? null,
      price: assignedWine?.price,
      totalPoints,
      rank: 0, // calculated below
      scoreDistribution,
    }
  })

  // Sort by total points (lower = better) for ranking
  const sorted = [...results].sort((a, b) => a.totalPoints - b.totalPoints)

  // Assign ranks with ties
  let currentRank = 1
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i].totalPoints > sorted[i - 1].totalPoints) {
      currentRank = i + 1
    }
    sorted[i].rank = currentRank
  }

  // Copy ranks back to original order
  for (const sortedResult of sorted) {
    results[sortedResult.index].rank = sortedResult.rank
  }

  return results
}

export function getIncompleteTasters(tasting: Tasting): Taster[] {
  return tasting.tasters.filter((taster) => taster.scores.some((score) => score === null))
}

export function isTasterComplete(taster: Taster): boolean {
  return taster.scores.every((score) => score !== null)
}
